import type { ElkExtendedEdge, ElkNode, ELK as ElkInstance } from 'elkjs';
import type { Node, Edge } from '@xyflow/react';
// Resolve the ELK worker through Vite's module graph (`?url`) so it is emitted
// as a content-hashed asset under /assets. (Previously copied via
// vite-plugin-static-copy, which emitted it to the wrong nested path under
// Vite 8 / Rolldown, so the worker URL 404'd.)
import elkWorkerUrl from 'elkjs/lib/elk-worker.min.js?url';

const DEFAULT_NODE_WIDTH = 200;
const DEFAULT_NODE_HEIGHT = 120;

export async function loadElk(): Promise<ElkInstance> {
  const ELK = (await import('elkjs/lib/elk-api.js')).default;
  return new ELK({
    workerUrl: elkWorkerUrl,
  });
}

export function computeLayout<T extends Node>(
  elkInstance: ElkInstance,
  nodes: T[],
  edges: Edge[],
): Promise<T[]> {
  const nodeOutputs = new Map<string, string[]>();
  const nodeInputs = new Map<string, string[]>();

  for (const node of nodes) {
    nodeOutputs.set(node.id, []);
    nodeInputs.set(node.id, []);
  }

  for (const edge of edges) {
    if (edge.sourceHandle) {
      const outputs = nodeOutputs.get(edge.source);
      if (outputs && !outputs.includes(edge.sourceHandle)) {
        outputs.push(edge.sourceHandle);
      }
    }
    if (edge.targetHandle) {
      const inputs = nodeInputs.get(edge.target);
      if (inputs && !inputs.includes(edge.targetHandle)) {
        inputs.push(edge.targetHandle);
      }
    }
  }

  const children: ElkNode[] = nodes.map((node) => {
    const w = node.measured?.width ?? DEFAULT_NODE_WIDTH;
    const h = node.measured?.height ?? DEFAULT_NODE_HEIGHT;
    const outputs = nodeOutputs.get(node.id) ?? [];
    const inputs = nodeInputs.get(node.id) ?? [];

    return {
      id: node.id,
      width: w,
      height: h,
      ports: [
        ...inputs.map((handleId) => ({
          id: `${node.id}::in::${handleId}`,
          layoutOptions: { 'elk.port.side': 'WEST' },
        })),
        ...outputs.map((handleId) => ({
          id: `${node.id}::out::${handleId}`,
          layoutOptions: { 'elk.port.side': 'EAST' },
        })),
      ],
      layoutOptions: {
        'elk.portConstraints': 'FIXED_SIDE',
      },
    };
  });

  const elkEdges: ElkExtendedEdge[] = edges.map((edge, i) => ({
    id: `e${i}`,
    sources: [
      edge.sourceHandle
        ? `${edge.source}::out::${edge.sourceHandle}`
        : edge.source,
    ],
    targets: [
      edge.targetHandle
        ? `${edge.target}::in::${edge.targetHandle}`
        : edge.target,
    ],
  }));

  return elkInstance
    .layout({
      id: 'root',
      children,
      edges: elkEdges,
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': 'RIGHT',
        'elk.spacing.nodeNode': '40',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        'elk.edgeRouting': 'POLYLINE',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.crossingMinimization.greedySwitch.type': 'TWO_SIDED',
        'elk.layered.thoroughness': '40',
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      },
    })
    .then((graph) => {
      const posMap = new Map<string, { x: number; y: number }>();
      for (const child of graph.children ?? []) {
        posMap.set(child.id, { x: child.x ?? 0, y: child.y ?? 0 });
      }

      return nodes.map((node): T => {
        const pos = posMap.get(node.id);
        if (!pos) return node;
        return { ...node, position: pos };
      });
    });
}
